import FormattedMessage from 'I18n/components/FormattedMessage';
import { getObjectTypeIconComponent } from 'customer-data-objects-ui-components/objects/getObjectTypeIconComponent';
import * as ObjectTypeIds from 'customer-data-objects/constants/ObjectTypeIds';
import { CommunicationIcon } from 'foundations-assets/icons/components/CommunicationIcon';
import { IntegrationsIcon } from 'foundations-assets/icons/components/IntegrationsIcon';
import { KnowledgeBaseIcon } from 'foundations-assets/icons/components/KnowledgeBaseIcon';
import { LogPostalMailIcon } from 'foundations-assets/icons/components/LogPostalMailIcon';
import { MessagesIcon } from 'foundations-assets/icons/components/MessagesIcon';
import { PublishingTaskIcon } from 'foundations-assets/icons/components/PublishingTaskIcon';
import { SprocketIcon } from 'foundations-assets/icons/components/SprocketIcon';

/**
 * The util, getObjectTypeIconComponent, is our main source of truth for Hubspot object icons
 * However, it supports a limited number of object types and we need to ensure we fill the gaps it is missing
 */
import { jsx as _jsx } from "react/jsx-runtime";
const getFallbackHubspotIcon = objectTypeId => {
  switch (objectTypeId) {
    case ObjectTypeIds.KNOWLEDGE_ARTICLE_TYPE_ID:
      return KnowledgeBaseIcon;
    case ObjectTypeIds.PUBLISHING_TASK_TYPE_ID:
      return PublishingTaskIcon;
    case ObjectTypeIds.CONVERSATION_SESSION_TYPE_ID:
      return MessagesIcon;
    case ObjectTypeIds.COMMUNICATION_TYPE_ID:
      return CommunicationIcon;
    case ObjectTypeIds.POSTAL_MAIL_TYPE_ID:
      return LogPostalMailIcon;
    default:
      return SprocketIcon;
  }
};

/**
 * We should investigate if we can get BE support for getting the display name for an object type id
 * The only option right now is `translateObjectTypeName` which requires fetching an object type's objectTypeDefinition
 * This would not work given that there can many multiple citations for a single prompt which would require multiple API fetches
 *
 * Figma file with list of objects we want supported: https://www.figma.com/design/BIKG7UobEoQG7LMDFtMu8k/AI-Experience-System?node-id=16301-16440&t=Lg2jSF3EDgj5mRrB-0
 */
const OBJECT_TYPE_ID_TO_DISPLAY_NAME = {
  [ObjectTypeIds.CONTACT_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.contact"
  }),
  [ObjectTypeIds.COMPANY_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.company"
  }),
  [ObjectTypeIds.DEAL_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.deal"
  }),
  [ObjectTypeIds.TICKET_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.ticket"
  }),
  [ObjectTypeIds.KNOWLEDGE_ARTICLE_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.knowledgeBase"
  }),
  [ObjectTypeIds.QUOTE_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.quote"
  }),
  [ObjectTypeIds.EMAIL_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.email"
  }),
  [ObjectTypeIds.TASK_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.task"
  }),
  [ObjectTypeIds.NOTE_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.note"
  }),
  [ObjectTypeIds.MEETING_EVENT_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.meeting"
  }),
  [ObjectTypeIds.CALL_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.call"
  }),
  [ObjectTypeIds.LINE_ITEM_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.lineItem"
  }),
  [ObjectTypeIds.PUBLISHING_TASK_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.publishingTask"
  }),
  [ObjectTypeIds.CONVERSATION_SESSION_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.conversationSession"
  }),
  [ObjectTypeIds.COMMUNICATION_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.communication"
  }),
  [ObjectTypeIds.POSTAL_MAIL_TYPE_ID]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.postalMail"
  })
};
export const safeDecodeURIComponent = value => {
  try {
    return decodeURIComponent(value);
  } catch (_unused) {
    return value;
  }
};
export const getCrmCitationObjectNameAndIcon = ({
  objectTypeId
}) => {
  if (!objectTypeId) {
    return {
      sourceName: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.unknownObject"
      }),
      icon: SprocketIcon
    };
  }
  // if the <meta type id> is '1' this indicates that it is an Integration-specific object i.e. integration-object
  else if (objectTypeId !== null && objectTypeId !== void 0 && objectTypeId.startsWith('1-')) {
    return {
      sourceName: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.integrationObject"
      }),
      icon: IntegrationsIcon
    };
  }
  // if the <meta type id> is '2' this indicates that it is a Hub-specific object i.e. custom-object
  else if (objectTypeId !== null && objectTypeId !== void 0 && objectTypeId.startsWith('2-')) {
    return {
      sourceName: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.customObject"
      }),
      // this util handles the case where the object type id starts with '2-' and returns the CustomObjectIcon
      icon: getObjectTypeIconComponent(objectTypeId)
    };
  }
  const HubspotObjectIcon = getObjectTypeIconComponent(objectTypeId, {
    DefaultIconComponent: getFallbackHubspotIcon(objectTypeId),
    overrides: {
      [ObjectTypeIds.POSTAL_MAIL_TYPE_ID]: LogPostalMailIcon
    }
  });
  return {
    sourceName: OBJECT_TYPE_ID_TO_DISPLAY_NAME[objectTypeId] || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "chatspotComponents.sections.markdownWithCitationsSectionFormatter.inlineCitation.unknownObject"
    }),
    icon: HubspotObjectIcon
  };
};